import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { AppComponent } from './app/app.component';
import { importProvidersFrom } from '@angular/core';
import {
  MsalModule,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
} from '@azure/msal-angular';
import {
  PublicClientApplication,
  BrowserCacheLocation,
  InteractionType,
} from '@azure/msal-browser';
import { environment } from './environments/environment';
import {
  DsFormFieldModule,
  DsHeaderModule,
  DsImprintModule,
  DsSelectModule,
} from '@bmw-ds/components';
import { RoleGuard } from './app/guards/role.guard';
import {
  HttpClient,
  HttpClientModule,
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  provideTranslateService,
  TranslateLoader,
  TranslateModule,
  TranslateService,
  TranslateStore,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  Component,
  AfterViewInit,
  ViewChild,
  ElementRef,
  Renderer2,
} from '@angular/core';
import { authInterceptor } from './app/auth.interceptor';
import { StatusAuthInterceptor } from './app/status-auth.interceptor';

const msalInstance = new PublicClientApplication({
  auth: {
    clientId: environment.msalConfig.clientId,
    authority: environment.msalConfig.authority,
    redirectUri: environment.msalConfig.redirectUri,
  },
  cache: {
    // cacheLocation: BrowserCacheLocation.LocalStorage,
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: true,
  },
});

const imprintProviders =
  DsImprintModule.forRoot({
    phone: '-',
    electronicContact: 'smartphone_rollout_werke@bmw.de',
  }).providers ?? [];

let providerId = '';
let providerUrl = '';
if ((environment.msalConfig.env = 'prod')) {
  providerId = 'api://e2d3fbfa-ffbd-48ad-8f2e-52e29a0170df/.default';
  providerUrl = 'https://bob-functionapp-prod.azurewebsites.net';
} else {
  providerId = 'api://b6494c7e-9986-45a2-bdfb-2f18531f316a/.default';
  providerUrl = 'https://blue-collar-functions-dev.azurewebsites.net';
}
// **Ensure MSAL handles redirects before bootstrapping Angular**
msalInstance
  .initialize()
  .then(() => msalInstance.handleRedirectPromise()) // ✅ Process login before Angular starts
  .then(() => {
    // console.log('✅ MSAL redirect processed, bootstrapping Angular app...');

    const account =
      msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];

    if (account) {
      // console.log('✅ User detected on initial login:', account);
      msalInstance.setActiveAccount(account);
    } else {
      // console.log('⚠️ No active account found after login processing.');
    }

    function HttpLoaderFactory(http: HttpClient) {
      return new TranslateHttpLoader(http, './assets/i18n/', '.json');
    }

    bootstrapApplication(AppComponent, {
      providers: [
        importProvidersFrom(DsFormFieldModule, DsSelectModule), // ✅ Ensure DS UI components are included
        importProvidersFrom(HttpClientModule),
        // ✅ ID token for status polling (/api/status/)
        {
          provide: HTTP_INTERCEPTORS,
          useClass: StatusAuthInterceptor,
          multi: true,
        },
        TranslateService,
        TranslateModule,
        provideTranslateService({
          defaultLanguage: 'en',
          loader: {
            provide: TranslateLoader,
            useFactory: HttpLoaderFactory,
            deps: [HttpClient],
          },
        }),
        // ✅ Provide HttpClient globally
        provideRouter(
          [
            { path: '', redirectTo: '/dashboard', pathMatch: 'full' },

            {
              path: 'dashboard',
              loadComponent: () =>
                import('./app/dashboard/dashboard.component').then(
                  (m) => m.DashboardComponent,
                ),
              canActivate: [MsalGuard],
            },

            // {
            //   path: 'profile',
            //   loadComponent: () => import('./app/profile/profile.component').then(m => m.ProfileComponent),
            //   canActivate: [MsalGuard]
            // },

            {
              path: 'self-service',
              loadComponent: () =>
                import('./app/self-service/self-service.component').then(
                  (m) => m.SelfServiceComponent,
                ),
              canActivate: [MsalGuard, RoleGuard],
              data: { requiresKiosk: false },
            },
            {
              path: 'kiosk',
              loadComponent: () =>
                import('./app/kiosk/kiosk.component').then(
                  (m) => m.KioskComponent,
                ),
              canActivate: [MsalGuard, RoleGuard],
              data: { requiresKiosk: true }, // Restrict to Kiosk users only
            },
            {
              path: 'meister',
              loadComponent: () =>
                import('./app/meister/meister.component').then(
                  (m) => m.MeisterComponent,
                ),
              canActivate: [MsalGuard, RoleGuard],
              data: { requiresMeister: true }, // Restrict to Meister users only
            },
            {
              path: 'history',
              loadComponent: () =>
                import('./app/history/history.component').then(
                  (m) => m.HistoryComponent,
                ),
              canActivate: [MsalGuard, RoleGuard],
            },
            {
              path: 'settings',
              loadComponent: () =>
                import('./app/settings/settings.component').then(
                  (m) => m.SettingsComponent,
                ),
              canActivate: [MsalGuard, RoleGuard],
            },
            {
              path: 'admin',
              loadComponent: () =>
                import('./app/admin/admin.component').then(
                  (m) => m.AdminComponent,
                ),
              canActivate: [MsalGuard, RoleGuard],
              data: { requiresAdmin: true }, // Restrict to Kiosk users only
            },
            { path: '**', redirectTo: '/dashboard' },
          ],
          withComponentInputBinding(),
        ),

        importProvidersFrom(
          MsalModule.forRoot(
            msalInstance,
            {
              interactionType: InteractionType.Redirect,
              authRequest: {
                scopes: ['user.read'],
              },
            },
            {
              interactionType: InteractionType.Redirect,
              protectedResourceMap: new Map([
                [providerUrl, [providerId]],
                ['https://graph.microsoft.com/v1.0/me', ['user.read']],
              ]),
            },
          ),
        ),

        MsalService,
        MsalGuard,
        MsalBroadcastService,
        DsHeaderModule,
        imprintProviders,
      ],
    })
      .then(() => {
        // DOM manipulation after Angular is bootstrapped
        const tryAppendImprint = () => {
          const imprint = document.querySelector('ds-imprint');
          if (imprint) {
            const div = document.createElement('div');
            div.textContent = 'Made with 🧡 by intellecom GmbH';
            div.style.fontStyle = 'italic';
            div.style.color = '#6c757d';
            div.style.fontSize = '0.95rem';
            div.style.marginTop = '20px';
            // div.style.padding = '0.5rem';
            imprint.appendChild(div);
          } else {
            setTimeout(tryAppendImprint, 100); // keep retrying until imprint appears
          }
        };

        tryAppendImprint();
      })
      .catch((err) => console.error('Angular bootstrap error:', err));
  });
